import { useCallback, useEffect, useRef, useState } from 'react';
import { useHovered } from 'foundations-components/transitional/utils/useHovered';
import { TRACKING_EVENT_NAME, TRACKING_NAMESPACE, TRACKING_SCREEN } from '../constants/tracking';
export function useCitationPopoverState({
  citationType,
  onTrack
}) {
  const {
    onMouseEnter,
    onMouseLeave
  } = useHovered({});
  const [open, setOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [hasTrackedHover, setHasTrackedHover] = useState(false);
  const closeDelayTimeoutRef = useRef(null);
  const openDelayTimeoutRef = useRef(null);
  useEffect(() => () => {
    if (closeDelayTimeoutRef.current) {
      clearTimeout(closeDelayTimeoutRef.current);
    }
    if (openDelayTimeoutRef.current) {
      clearTimeout(openDelayTimeoutRef.current);
    }
  }, []);
  useEffect(() => {
    if (!open) {
      setIsSticky(false);
    }
  }, [open]);
  const clearCloseDelayTimeout = useCallback(() => {
    if (closeDelayTimeoutRef.current) {
      clearTimeout(closeDelayTimeoutRef.current);
      closeDelayTimeoutRef.current = null;
    }
  }, []);
  const clearOpenDelayTimeout = useCallback(() => {
    if (openDelayTimeoutRef.current) {
      clearTimeout(openDelayTimeoutRef.current);
      openDelayTimeoutRef.current = null;
    }
  }, []);
  const handleButtonMouseEnter = useCallback(e => {
    if (!hasTrackedHover) {
      onTrack === null || onTrack === void 0 || onTrack(TRACKING_NAMESPACE, TRACKING_EVENT_NAME, {
        screen: TRACKING_SCREEN,
        action: 'citation hover',
        type: citationType
      });
      setHasTrackedHover(true);
    }
    onMouseEnter === null || onMouseEnter === void 0 || onMouseEnter(e);
    if (isSticky) {
      return;
    }
    clearCloseDelayTimeout();
    clearOpenDelayTimeout();
    openDelayTimeoutRef.current = window.setTimeout(() => {
      setOpen(true);
    }, 200);
  }, [hasTrackedHover, onMouseEnter, isSticky, clearCloseDelayTimeout, clearOpenDelayTimeout, onTrack, citationType]);
  const handleButtonMouseLeave = useCallback(e => {
    onMouseLeave === null || onMouseLeave === void 0 || onMouseLeave(e);
    if (isSticky) {
      return;
    }
    clearOpenDelayTimeout();
    closeDelayTimeoutRef.current = window.setTimeout(() => {
      setOpen(false);
    }, 200);
  }, [isSticky, clearOpenDelayTimeout, onMouseLeave]);
  const handlePopoverMouseEnter = useCallback(() => {
    if (isSticky) {
      return;
    }
    clearCloseDelayTimeout();
  }, [isSticky, clearCloseDelayTimeout]);
  const handlePopoverMouseLeave = useCallback(() => {
    if (isSticky) {
      return;
    }
    setOpen(false);
  }, [isSticky]);
  const handlePopoverClick = useCallback(() => {
    setIsSticky(true);
    clearCloseDelayTimeout();
  }, [clearCloseDelayTimeout]);
  const handleOpenChange = useCallback(event => {
    setOpen(event.target.value);
    if (!event.target.value) {
      setIsSticky(false);
      clearCloseDelayTimeout();
      clearOpenDelayTimeout();
    }
  }, [clearCloseDelayTimeout, clearOpenDelayTimeout]);
  return {
    open,
    handleButtonMouseEnter,
    handleButtonMouseLeave,
    handlePopoverMouseEnter,
    handlePopoverMouseLeave,
    handlePopoverClick,
    handleOpenChange
  };
}